<template>
  <div class="panel place-detail-info">
    <div class="panel-content">
      <div class="tables">
        <!-- 標準項目 -->
        <data-list-table
          v-bind:id="'place-data-list-table-standard'"
          v-bind:data="standardData"
        >
          <template v-slot:relations="{ item }">
            <div class="user-content-wrapper">
              <span
                v-for="(d, index) in item.value"
                v-bind:key="`${d.type}_${d.organization_id || d.user_id}`"
                class="name"
              >
                <!-- グループ -->
                <text-organization
                  v-if="d.type === 'organization'"
                  v-bind:name="d.organization_name"
                  v-bind:users="d.organization_user_relations"
                  v-bind:user-name-field="'user_name'"
                />
                <!-- ユーザー -->
                <span v-else v-text="d.user_name" />
                <!-- カンマ -->
                <span
                  v-if="index !== item.value.length - 1"
                  class="trailing-comma"
                  v-text="','"
                />
              </span>
            </div>
          </template>
        </data-list-table>
        <!-- プレイス属性 -->
        <data-list-table
          v-bind:id="'place-data-list-table-attribute'"
          v-bind:data="attributeData"
          class="following-table"
        />
        <!-- 添付ファイル -->
        <data-list-table
          v-if="attachFilesData.length"
          v-bind:id="'place-data-list-table-attach-files'"
          v-bind:data="attachFilesData"
          class="attach-file-data-list-table following-table"
        >
          <template v-slot:attach-file="{ item }">
            <attach-file-viewer
              v-bind:attachFiles="item.value"
              v-bind:recordId="placeId"
              class="viewer"
            />
          </template>
        </data-list-table>
      </div>
      <hr class="separator" />
      <div class="history">
        <p class="item">
          <span class="label" v-t="'appPanelPlaceDetailInfo.label.created'" />
          <span class="text">
            {{ historyMessage.created }}
          </span>
        </p>
        <p class="item">
          <span
            class="label"
            v-t="'appPanelPlaceDetailInfo.label.lastUpdated'"
          />
          <span class="text">
            {{ historyMessage.updated }}
          </span>
        </p>
      </div>
    </div>
    <div
      class="fix-action-sheet"
      v-bind:class="{ fixed: positionFixData.isFixed }"
    >
      <p class="text">{{ positionFixData.message }}</p>
      <b-button
        size="sm"
        variant="secondary"
        v-bind:disabled="positionFixData.isFixed"
        v-on:click="$_onFixClick"
      >
        {{ positionFixData.action }}
      </b-button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { DataListTable } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import AttachFileViewer from '@/components/AppAttachFileViewer.vue'
import {
  DATE_FORMAT_SLASH,
  TIME_FORMAT_MINUTE,
  DEFAULT_AUTO_CHECKIN_RANGE_OUTDOOR,
  DEFAULT_AUTO_CHECKIN_RANGE_INDOOR,
} from '@/constants.js'
import GeometryUtil from '@/js/util/GeometryUtil.js'

// 表示時刻フォーマット
const DATE_FORMAT = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_MINUTE}`

export default {
  name: 'AppPanelPlaceDetailInfo',
  components: {
    'data-list-table': DataListTable,
    'attach-file-viewer': AttachFileViewer,
  },
  props: {
    /**
     * プレイスデータ
     */
    place: {
      type: Object,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  computed: {
    /**
     * プレイスID
     * @type {Number}
     */
    placeId: function () {
      return (this.place && this.place.id) || null
    },
    /**
     * 重心位置
     * @type {Object}
     */
    centroid: function () {
      if (!this.place) return null
      const geometry = this.place.geom.geometry
      if (geometry.type === 'Point') {
        return { lat: geometry.coordinates[1], lng: geometry.coordinates[0] }
      } else {
        // Point以外は重心とする
        const centroid = GeometryUtil.centroid(geometry)
        return { lat: centroid[1], lng: centroid[0] }
      }
    },
    /**
     * 詳細表示用データ(標準項目分)
     * @type {Object}
     */
    standardData: function () {
      if (!this.place) return []
      const data = [
        {
          label: this.$t('appPanelPlaceDetailInfo.field.group'),
          value: this.place.place_groups
            .map((g) => g.place_group_name)
            .join(','),
        },
        {
          name: 'relations',
          label: this.$t('appPanelPlaceDetailInfo.field.relatedUser'),
          value: [
            ...(this.place.place_organization_relations || []).map((o) => ({
              type: 'organization',
              ...o,
            })),
            ...(this.place.place_user_relations || []).map((u) => ({
              type: 'user',
              ...u,
            })),
          ],
          type: ATTRIBUTE_TYPE.SLOT,
        },
        {
          label: this.$t('appPanelPlaceDetailInfo.field.placeCode'),
          value: this.place.place_code,
        },
        {
          label: this.$t('appPanelPlaceDetailInfo.field.postalCode'),
          value: this.place.postal_code,
        },
        {
          label: this.$t('appPanelPlaceDetailInfo.field.address'),
          value: `${this.place.address1 || ''} ${this.place.address2 || ''}`,
        },
        [
          {
            label: this.$t('appPanelPlaceDetailInfo.field.tel'),
            value: this.place.tel,
          },
          {
            label: this.$t('appPanelPlaceDetailInfo.field.fax'),
            value: this.place.fax,
          },
        ],
        {
          label: this.$t('appPanelPlaceDetailInfo.field.location'),
          value: this.$t(
            'appPanelPlaceDetailInfo.location',
            this.centroid || { lat: '', lng: '' }
          ),
        },
        {
          label: this.$t('appPanelPlaceDetailInfo.field.autoCheckinRange'),
          value:
            this.place.checkin_radius != null
              ? this.place.checkin_radius
              : this.place.indoor_level_id
                ? DEFAULT_AUTO_CHECKIN_RANGE_INDOOR
                : DEFAULT_AUTO_CHECKIN_RANGE_OUTDOOR,
        },
        {
          label: this.$t('appPanelPlaceDetailInfo.field.remarks'),
          value: this.place.remarks,
          type: ATTRIBUTE_TYPE.STRING,
        },
      ]
      if (this.place.indoor_level_id) {
        data.splice(2, 0, {
          label: this.$t('appPanelPlaceDetailInfo.field.indoorLevel'),
          value: this.place.indoor_level_name,
        })
      }

      return data
    },
    /**
     * 詳細表示用データ(プレイス属性分)
     * @type {Array.<Object>}
     */
    attributeData: function () {
      if (!this.place || !this.place.place_attributes) return []
      return this.place.place_attributes.map((attr) => ({
        label: attr.attribute_name,
        type: attr.attribute_type,
        value: attr.attribute_value,
      }))
    },
    /**
     * 詳細表示用データ(添付ファイル分)
     * @type {Array.<Object>}
     */
    attachFilesData: function () {
      if (!this.place) return []
      return [
        {
          name: 'attach-file',
          label: this.$t('appPanelPlaceDetailInfo.field.attachFiles'),
          value: this.place.place_attach_files,
          type: ATTRIBUTE_TYPE.SLOT,
        },
      ]
    },
    /**
     * 登録更新履歴メッセージ
     * @type {Object}
     */
    historyMessage: function () {
      const created = this.$t('appPanelPlaceDetailInfo.message.created', {
        date: this.place
          ? moment(this.place.create_at).format(DATE_FORMAT)
          : '',
        user: this.place ? this.place.create_user_name : '',
      })
      const updated = this.$t('appPanelPlaceDetailInfo.message.lastUpdated', {
        date: this.place
          ? moment(this.place.update_at).format(DATE_FORMAT)
          : '',
        user: this.place ? this.place.update_user_name : '',
      })
      return { created, updated }
    },
    /**
     * 位置確定データ
     * @type {Object}
     */
    positionFixData: function () {
      if (!this.place) {
        return { isFixed: null, message: '', action: '' }
      }
      const { position_fix_flg, fix_at, fix_user_name } = this.place
      const fixAt = moment(fix_at)
      const message = position_fix_flg
        ? this.$t('appPanelPlaceDetailInfo.message.fixed', {
            date: fixAt.isValid()
              ? fixAt.format(DATE_FORMAT)
              : this.$t('appPanelPlaceDetailInfo.unknownDate'),
            user:
              fix_user_name || this.$t('appPanelPlaceDetailInfo.unknownUser'),
          })
        : this.$t('appPanelPlaceDetailInfo.message.unfixed')
      const action = position_fix_flg
        ? this.$t('appPanelPlaceDetailInfo.fixed')
        : this.$t('appPanelPlaceDetailInfo.action.fix')
      return {
        isFixed: position_fix_flg,
        message,
        action,
      }
    },
  },
  methods: {
    /**
     * 位置確定ボタンクリック時処理
     */
    $_onFixClick: function () {
      /**
       * 位置確定クリックイベント
       * @param {Number} id プレイスID
       */
      this.$emit('position-fix-click', this.placeId)
    },
  },
}
</script>

<style lang="scss" scoped>
$-fixed-sheet-height: calc(2rem + 20px);

.panel {
  width: 100%;
  height: 100%;
  color: $text-color;
  background-color: $dialog-background-color;
}

.panel-content {
  height: calc(100% - #{$-fixed-sheet-height});
  padding: 22px 22px 0 22px;
  overflow-y: auto;
  @include mixin-scrollbar;
  & {
    border-right: 1px solid #f0f0f0;
  }
}

.following-table {
  margin-top: -1px;
}

.attach-file-data-list-table {
  ::v-deep .data-list-li:first-child {
    border-top: 1px solid #a0a0a0;
  }
  .viewer {
    margin: 8px;
  }
}

.separator {
  border-top: 1px solid #cccccc;
}

.history {
  font-size: $font-size-small;
  margin-bottom: 1rem;

  .item {
    margin: 0;
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;

    .label {
      flex-shrink: 0;
      flex-wrap: nowrap;
    }
  }
}

.fix-action-sheet {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: $-fixed-sheet-height;
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-bottom: none;
  background-color: #ffffff;

  &.fixed {
    background-color: #6e757c;
    color: $entity-color-white;
  }

  .text {
    margin: 0;
    font-size: $font-size-middle;
    word-break: normal;
  }

  .btn {
    flex-shrink: 0;
  }
}

.user-content-wrapper {
  padding: 8px;
  display: inline-block;
  width: 100%;
  word-break: break-word;

  .name {
    display: inline-block;

    ::v-deep .text {
      font-size: $font-size-small;
    }

    .trailing-comma {
      font-size: $font-size-small;
      margin-right: calc(#{$font-size-small} / 4);
    }
  }
}
</style>
